import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints, MediumType } from '@/api';
import { useFetch } from '@/utils/fetch';

export type Medium = {
  id: string;
  type: MediumType;
  originalPath: string | null;
  url: string;
  size: number;
  undeletable?: boolean;
  deletedAt: string | null;
};

export const isImage = (medium: { originalPath: string | null }) =>
  medium.originalPath ? /\.(jpg|jpeg|png|webp|jfif)$/i.test(medium.originalPath) : false;

export type MediumFromGetMany = Endpoints['GET /media']['response']['media'][number];
export const useMedia = (
  params: Endpoints['GET /media']['query'],
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {
    enabled: true,
  }
) => {
  const fetch = useFetch<Endpoints['GET /media']>();

  return useQuery({
    queryKey: ['media', params],
    queryFn: () => fetch('/media', params),
    enabled:
      options.enabled &&
      (!!params.requestId ||
        !!params.articleId ||
        !!params.userId ||
        !!params.articleConditionId ||
        !!params.articleDefectId ||
        !!params.articleActionId ||
        !!params.invoiceId),
    // A media is mostly never updated, so we can safely disable the automatic refetch
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: options.refetchInterval,
  });
};

export const useMediaQueries = (paramsList: Endpoints['GET /media']['query'][]) => {
  const fetch = useFetch<Endpoints['GET /media']>();

  return useQueries({
    queries: paramsList.map((params) => ({
      queryKey: ['media', params],
      queryFn: () => fetch('/media', params),
      enabled: !!params.articleId || !!params.userId,
    })),
  });
};

export type CreateMediumBody = Endpoints['POST /media']['body'];

export const useCreateMedium = (headers?: HeadersInit) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /media']>();

  return useMutation({
    mutationFn: (body: CreateMediumBody & { file: File }) =>
      fetch(
        '/media',
        undefined,
        {
          headers: headers,
          method: 'POST',
          body: Object.fromEntries(
            Object.entries(body).filter(([, value]) => value !== undefined)
          ) as CreateMediumBody,
        },
        'multipart/form-data'
      ),
    onSettled: (_data, _error, body) => {
      if (body.articleConditionId || body.articleConditionFormId) {
        queryClient.invalidateQueries({ queryKey: ['articles', body.articleId, 'condition-form'] });
      }
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });

      if (body.articleId) {
        queryClient.invalidateQueries({ queryKey: ['requests'] });
      }

      if (body.invoiceId) {
        queryClient.invalidateQueries({ queryKey: ['invoices', body.invoiceId] });
      }
    },
  });
};

export const useDeleteMedium = (articleId?: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch();

  return useMutation({
    mutationFn: (id: string) => fetch(`/media/${id}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      if (articleId) {
        queryClient.invalidateQueries({ queryKey: ['articles', articleId, 'condition-form'] });
      }
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};
